import { Injectable } from '@angular/core';
import { WikiService } from './wiki.service';
import { WikiConstants, WikiContentFormat } from '../../../plugins/wiki/wiki.constants';

/** Result of content loading or conversion */
export interface WikiContentResult {
    editableContent: string;
    content_type: WikiContentFormat;
    wasConvertedFromHtml: boolean;
    convertedMarkdownContent: string;
    /** The page version this content came from. Sent back as `baseVersion` on save so the
     *  server can reject the write if someone else saved in the meantime. 0 when unknown. */
    version?: number;
    /** Conversion quality score 0.0–1.0 (only set after HTML→Markdown conversion) */
    conversionQuality?: number;
    /** Original HTML before conversion (only set after HTML→Markdown conversion) */
    originalHtml?: string;
    /** Warnings from the conversion validator */
    conversionWarnings?: string[];
}

/**
 * Identifies which wiki a save targets. A wiki page is keyed by `(section, refId)`;
 * `{ project: unixName }` is backend sugar the API resolves to `section='project',
 * refId=<projectId>`. Both forms are accepted interchangeably by `POST /api/wiki/{id}`.
 */
export type WikiScope =
    | { project: string }
    | { section: string; refId: number };

/** Payload sent when saving a wiki page */
export interface WikiSavePayload {
    content: string;
    pagename: string;
    content_type: string;
    /** Project unix-name scope (mutually exclusive with section/refId). */
    project?: string;
    /** Section scope, e.g. 'project' | 'admin' | 'user' (used with refId). */
    section?: string;
    /** Id of the section entity (project id, 0 for the site homepage). */
    refId?: number;
    attachmentIds?: number[];
    /** Optional short note describing what changed in this version, shown in the Versions tab. */
    changeSummary?: string;
    /** The version the editor loaded. Required by the server on every save that updates an
     *  existing page; optional only when this payload creates one, which is the sole reason
     *  the field is still optional here. */
    baseVersion?: number;
}

/**
 * Service that encapsulates wiki edit logic:
 *  - Loading content (raw markdown or HTML→markdown conversion)
 *  - Saving wiki pages (with content_type in payload)
 */
@Injectable()
export class WikiEditService {

    constructor(
        private wikiService: WikiService,
    ) {}

    // ───────────────────────── content loading ──────────────────────────

    /**
     * Auto-convert an HTML wiki page to Markdown via the backend API.
     * Returns the converted content result.
     */
    convertHtmlToMarkdown(wikiId: number): Promise<WikiContentResult> {
        return this.wikiService.convertToMarkdown(wikiId)
            .then((response: any) => {
                const convertedMarkdown = this.cleanConvertedMarkdown(response.markdown || '');

                return {
                    editableContent: convertedMarkdown,
                    content_type: WikiConstants.ContentFormat.MARKDOWN,
                    wasConvertedFromHtml: true,
                    convertedMarkdownContent: convertedMarkdown,
                    version: response.version ?? 0,
                    conversionQuality: response.quality ?? 0,
                    originalHtml: response.originalHtml || '',
                    conversionWarnings: response.validation?.warnings || [],
                };
            });
    }

    /**
     * Load raw markdown content for editing (not rendered HTML).
     */
    loadRawMarkdownContent(wikiId: number): Promise<WikiContentResult> {
        return this.wikiService.getContent(wikiId, { editable: true, skipHitCount: true })
            .then((response: any) => {
                const rawContent = response.editableContent || response.data || '';
                return {
                    editableContent: rawContent,
                    content_type: response.content_type || WikiConstants.ContentFormat.MARKDOWN,
                    wasConvertedFromHtml: false,
                    convertedMarkdownContent: '',
                    version: response.version ?? 0
                };
            });
    }

    // ──────────────────────────── saving ─────────────────────────────

    /**
     * Build the save payload with `content_type` always included.
     * Defaults to MARKDOWN but callers can override when content is still HTML.
     * `scope` targets the wiki by project unix-name or by section/refId — the
     * backend treats the two forms identically (see {@link WikiScope}).
     */
    buildSavePayload(content: string, pageName: string, scope: WikiScope, content_type: WikiContentFormat = WikiConstants.ContentFormat.MARKDOWN, baseVersion?: number): WikiSavePayload {
        const payload: WikiSavePayload = {
            content,
            pagename: pageName,
            content_type,
            ...scope
        };
        // Sent whenever we know it. Absent means "this creates the page", the only case the
        // server accepts without one — so a save against an existing page that somehow got here
        // without a version is rejected with a 400 rather than quietly overwriting it.
        if (baseVersion && baseVersion > 0) {
            payload.baseVersion = baseVersion;
        }
        return payload;
    }

    /**
     * Save wiki content. Returns the server response.
     */
    saveWikiContent(wikiId: number, payload: WikiSavePayload): Promise<any> {
        return this.wikiService.update(wikiId, payload);
    }

    // ────────────────── safe markdown retrieval (3.1) ───────────────

    /**
     * Safely get current markdown from the editor.
     * Returns `null` when the editor is not ready — callers should NOT fall back
     * to wikiPage.rel.content.data because that may be HTML.
     */
    getEditorMarkdown(editor: { getMarkdown(): string } | null | undefined): string | null {
        if (!editor) return null;
        return this.cleanConvertedMarkdown(editor.getMarkdown());
    }

    /**
     * Clean up backslash escaping in markdown produced by ToastUI's
     * common_escape, which adds backslashes before [, ], |, #, >, etc.
     * every time editor.getMarkdown() is called. 
     */
    cleanConvertedMarkdown(md: string): string {
        // 1) Restore fully escaped wiki links:  \[\[...\]\]  →  [[...]]
        md = md.replace(/\\\[\\\[([^\]]*?)\\\]\\\]/g, (_, inner) => {
            return '[[' + inner.replace(/\\\|/g, '|') + ']]';
        });

        // 2) Restore partially escaped wiki links:  \[[...\]]  →  [[...]]
        //    (only outer brackets escaped — can happen from a single-pass escape)
        md = md.replace(/\\\[\[([^\]]*?)\\\]\]/g, (_, inner) => {
            return '[[' + inner.replace(/\\\|/g, '|') + ']]';
        });

        // 3) Unescape pipes inside remaining wiki links: [[Page \| Display]] → [[Page | Display]]
        md = md.replace(/\[\[([^\]]*?)\]\]/g, (match, inner) => {
            const cleaned = inner.replace(/\\\|/g, '|');
            return cleaned !== inner ? '[[' + cleaned + ']]' : match;
        });

        // 4) Unescape heading markers at line start: \## → ##
        md = md.replace(/^\\(#{1,6}\s)/gm, '$1');

        // 5) Unescape list markers at line start: \- → -
        md = md.replace(/^\\(-\s)/gm, '$1');

        return md;
    }
}
